import { useApi } from '@/api';
import { ChartTimeFilter, ExpandableKpiCard } from '@/components';
import { useChartFillDatasetsDates, useSparkColor } from '@/hooks';
import {
  CurrencyValue,
  Flex,
  formatNumber,
  KpiCard,
  KpiSkeleton,
  LineChart,
  Text,
  useIsMobile
} from '@blockanalitica/ui';
import { SimpleChartData } from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

type DataResponse = {
  address: string;
  network: string;
  wallet_address: string;
  asset_symbol: string;
  asset_address: string;
  asset_name: string;
  symbol: string;
  name: string;
  principal: string;
  principal_change: string;
  assets: string;
  assets_change: string;
  assets_usd: string;
  assets_usd_change: string;
  profit: string;
  profit_change: string;
  profit_total: string;
  profit_total_change: string;
  profit_total_usd: string;
  profit_total_usd_change: string;
  profit_estimated: string;
  profit_estimated_change: string;
  profit_estimated_usd: string;
  profit_estimated_usd_change: string;
  rate: string;
  rate_change: string;
  profit_claimed_total: string;
  historic: SimpleChartData[];
  underlying_rate: string;
  asset_rate: string;
  real_profit_estimated: string;
  real_profit_estimated_change: string;
  real_rate: string;
};

export default function ProjectedRevenue() {
  const color = useSparkColor();
  const isMobile = useIsMobile();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const { network, address, tokenAddress } = useParams();
  const [data, error] = useApi<DataResponse>({
    url: `/sparkstar/sll/tokens/${network}/${address}/${tokenAddress}/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const datasets = [
    {
      xField: 'date',
      yField: 'real_profit_estimated',
      borderColor: color
    }
  ];

  const chartData = useChartFillDatasetsDates(
    data?.historic,
    datasets,
    chartDaysAgo
  );

  const valueFormatter = useCallback((value: string | number) => {
    return <CurrencyValue value={value} variant="dynamic" />;
  }, []);

  const customConfig = useMemo(
    () => ({
      options: {
        aspectRatio: 2.5
      }
    }),
    []
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  return (
    <ExpandableKpiCard
      hoverBorderColor={color}
      text={
        <Flex gap="3xs">
          Projected Annual Revenue
          <Text fontSize="-1">(next 12 months)</Text>
        </Flex>
      }
      key={`revenue`}
      defaultShowChart={!isMobile}
      expandText="details"
      kpiCard={
        <KpiCard
          data={{
            real_profit_estimated: data['real_profit_estimated'],
            real_profit_estimated_change: data['real_profit_estimated_change']
          }}
          field="real_profit_estimated"
          variant="nestedAsChild"
          options={{
            notation: 'compact'
          }}
        />
      }>
      {/* <Text fontSize="-1" color="muted">
        The projected annual revenue is calculated based on the current value of
        the asset and current APY for the next 12 months.
      </Text>
      <Flex flexDirection="column" gap="2xs">
        <Flex flexDirection="row" gap="s" justifyContent="space-between">
          <Flex flexDirection="row" gap="3xs">
            <Text>Annual Yield:</Text>
          </Flex>
          <Flex flexDirection="row" gap="3xs">
            {data.underlying_rate && (
              <>
                <PercentageValue value={data['underlying_rate']} /> +
                <PercentageValue value={data['asset_rate']} /> =
              </>
            )}
            <PercentageValue value={data['real_rate']} />
          </Flex>
        </Flex>
        <Flex flexDirection="row" gap="s" justifyContent="space-between">
          <Text>Projected Monthly revenue:</Text>
          <CurrencyValue value={Number(data['profit_estimated']) / 12} />
        </Flex>
      </Flex>

      <Card backgroundColor="foreground">
       
      </Card> */}
      <LineChart
        data={chartData}
        datasets={datasets}
        valueFormatter={valueFormatter}
        config={customConfig}
        showValuesInTooltip={true}
        tooltip={{
          valueFormatter: (value) => {
            return formatNumber(Number(value));
          },
          labelFormatter: () => 'Total',
          displayColors: false
        }}
        timeFilter={
          <ChartTimeFilter
            activeOption={chartDaysAgo}
            onChange={setChartDaysAgo}
          />
        }
      />
    </ExpandableKpiCard>
  );
}
